var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Editor, EditorState, ContentState, convertFromHTML, Modifier, SelectionState, RichUtils, CompositeDecorator, Entity } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import React, { useState, useMemo, createElement, useEffect, useRef } from 'react';
import Popover from 'react-tiny-popover';
import { TextRibbon } from '../ui/text-ribbon';
import { useDebounce } from '../util';
export const RichEditor = (_a) => {
    var { value, onChange, tagName } = _a, wrapperProps = __rest(_a, ["value", "onChange", "tagName"]);
    const editor = useRef();
    const [focused, setFocused] = useState(false);
    const [editorState, setEditorState] = useState(value.__html
        ? EditorState.createWithContent(useMemo(() => importHtml(value), []), linkDecorator)
        : EditorState.createEmpty(linkDecorator));
    let mounted = false;
    useEffect(() => {
        if (!mounted) {
            return;
        }
        mounted = true;
        setEditorState(EditorState.forceSelection(EditorState.createWithContent(importHtml(value), linkDecorator), editorState.getSelection()));
    }, [value, editorState]);
    const dispatchChange = useDebounce(1000, (change) => {
        onChange({
            __html: stateToHTML(change.getCurrentContent())
        });
    }, [onChange]);
    const update = (change) => {
        if (change.getCurrentContent() !== editorState.getCurrentContent()) {
            dispatchChange(change);
        }
        setEditorState(change);
    };
    const cursorStyle = {
        strong: editorState.getCurrentInlineStyle().has('BOLD'),
        em: editorState.getCurrentInlineStyle().has('ITALIC'),
        link: extractLink(editorState)
    };
    const setCursorStyle = (style) => {
        let next = editorState;
        next = (style.em === cursorStyle.em) ? next : setInlineStyle(next, 'ITALIC', style.em);
        next = (style.strong === cursorStyle.strong) ? next : setInlineStyle(next, 'BOLD', style.strong);
        next = (style.link === cursorStyle.link) ? next : setLink(next, style.link);
        update(next);
    };
    const restoreFocus = () => {
        setFocused(true);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Popover, { containerStyle: { overflow: 'visible' }, align: "start", position: "top", isOpen: focused, content: React.createElement(TextRibbon, { value: cursorStyle, onChange: setCursorStyle, onModalClosed: restoreFocus }) }, createElement(tagName, wrapperProps, React.createElement(Editor, { ref: editor, onBlur: () => setFocused(false), onFocus: () => setFocused(true), editorState: editorState, onChange: update })))));
};
const findLinkEntities = (contentBlock, callback) => {
    contentBlock.findEntityRanges((character) => {
        const entityKey = character.getEntity();
        return (entityKey !== null &&
            Entity.get(entityKey).getType() === 'LINK');
    }, callback);
};
const Link = (props) => {
    const { url } = Entity.get(props.entityKey).getData();
    return (React.createElement("a", { href: url, onClick: e => e.preventDefault() }, props.children));
};
const linkDecorator = new CompositeDecorator([
    {
        strategy: findLinkEntities,
        component: Link,
    },
]);
const setLink = (editorState, url) => {
    if (url) {
        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', { url });
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });
        return RichUtils.toggleLink(newEditorState, expandCollapsedSelectionToWord(editorState), entityKey);
    }
    else {
        let selection = editorState.getSelection();
        const contentState = editorState.getCurrentContent();
        const startKey = selection.getStartKey();
        const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
        selection = setStartOffset(selection, 0);
        selection = setEndOffset(selection, blockWithLinkAtBeginning.getLength());
        return RichUtils.toggleLink(editorState, selection, null);
    }
};
const extractLink = (editorState) => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const startKey = selection.getStartKey();
    const startOffset = selection.getStartOffset();
    const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
    const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
    if (linkKey) {
        const linkInstance = contentState.getEntity(linkKey);
        return linkInstance.getData().url || undefined;
    }
    else {
        return undefined;
    }
};
const setInlineStyle = (state, style, active) => {
    const selection = expandCollapsedSelectionToWord(state);
    const nextContent = active
        ? Modifier.applyInlineStyle(state.getCurrentContent(), selection, style)
        : Modifier.removeInlineStyle(state.getCurrentContent(), selection, style);
    return EditorState.forceSelection(EditorState.createWithContent(nextContent, linkDecorator), state.getSelection());
};
const expandCollapsedSelectionToWord = (state) => {
    let sel = state.getSelection();
    if (!sel.isCollapsed()) {
        return sel;
    }
    const content = state.getCurrentContent().getBlockForKey(sel.getStartKey()).getText();
    while (sel.getStartOffset() > 0 && !isWhitespace(content[sel.getStartOffset() - 1])) {
        sel = setStartOffset(sel, sel.getStartOffset() - 1);
    }
    while (sel.getEndOffset() < content.length && !isWhitespace(content[sel.getEndOffset()])) {
        sel = setEndOffset(sel, sel.getEndOffset() + 1);
    }
    return sel;
};
const isWhitespace = (x) => Boolean(x.match(/\s/));
const setStartOffset = (state, offset) => {
    return new SelectionState(state.getIsBackward() ? state.set('focusOffset', offset) : state.set('anchorOffset', offset));
};
const setEndOffset = (state, offset) => {
    return new SelectionState(state.getIsBackward() ? state.set('anchorOffset', offset) : state.set('focusOffset', offset));
};
const importHtml = ({ __html }) => {
    const blocksFromHTML = convertFromHTML(__html);
    return ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
};
//# sourceMappingURL=rich-editor.js.map